import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text, Button } from 'v3/components';
import { cooperplaceApi } from 'services/api';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useModal } from 'hooks';
import { styles } from './styles';
import QuestionCard from './QuestionCard';
import RegisterVehicleModal from 'v3/pages/Travel/Register/Modals/RegisterVehicle';
import { RegisterWagon } from 'v3/pages/Travel/Register/Modals/RegisterWagon';
import {
  safeStr,
  hasBackAttachments,
  hasLocalFiles,
  isAnswered,
  isAttachmentRequiredByBackend,
  getQuestionSlug,
} from './Utils/helper';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  isParentQuestion,
  shouldShowSubQuestion,
  groupQuestionsWithSubQuestions,
} from 'v3/pages/Checklist/CheckListSingle/utils/questionTree';
import { collectFilesToUpload, appendFilesToFormData } from 'v3/pages/Checklist/CheckListSingle/utils/collectFilesToUpload';

function processParentQuestion(question, allQuestions) {

  const subQuestion = allQuestions.find(q => q.fatherQuestionId === question.id);
  if (!subQuestion) return question;

  const triggerValueIds = new Set(
    (question.questionSubResponses || [])
      .map((item) => item?.questionValueId)
      .filter(Boolean)
  );

  const processedResponses = (question.responses || []).map(response => {

    const links = Array.isArray(response?.subValueResponse) ? response.subValueResponse : [];
    const triggersSubQuestion = links.some(link => {
      return (
        link?.questionResponseId === subQuestion.fatherQuestionId ||
        link?.questionResponseId === subQuestion.id
      );
    });

    return {
      ...response,
      triggerSubQuestion:
        response?.triggerSubQuestion ||
        triggersSubQuestion ||
        (response?.id ? triggerValueIds.has(response.id) : false),
    };
  });

  return {
    ...question,
    responses: processedResponses,
    questionTrigger: processedResponses.filter(r => r.triggerSubQuestion),
  };
}

function normalizePlateAnswer(question) {
  const slug = getQuestionSlug(question);
  const plateFromLink =
    slug === 'vehicle_plate'
      ? question?.vehicle?.plate
      : (slug === 'wagon_plate' || slug === 'implement_plate')
        ? question?.wagon?.plate
        : null;

  const base = safeStr(question?.questionDescriptiveResponse || plateFromLink);
  const upper = base.trim() ? base.toUpperCase() : base;

  return {
    ...question,
    questionDescriptiveResponse: upper,
  };
}

export default function ResponseChecklistModal({
  open,
  onClose,
  id,
  checklistTitle,
  onSubmit,
  onAnnul,
  onSuccess,
  questions: injectedQuestions = null,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [model, setModel] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responseSaved, setResponseSaved] = useState(false);
  const submitInFlightRef = useRef(false);
  const registerVehicle = useModal();
  const registerWagon = useModal();
  const [currentVehicleQuestionId, setCurrentVehicleQuestionId] = useState(null);
  const [currentWagonQuestionId, setCurrentWagonQuestionId] = useState(null);
  const snackbar = useSnackbar();
  const groupedQuestions = useMemo(() => groupQuestionsWithSubQuestions(questions), [questions]);
  const total = useMemo(() => groupedQuestions.length, [groupedQuestions]);

  const answeredCount = useMemo(() => {
    return groupedQuestions.reduce((acc, { parent, subQuestions }) => {
      const parentAnswered = isAnswered(parent);
      const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));
      const subsOk = visibleSubs.every((sq) => !sq.required || isAnswered(sq));
      return acc + (parentAnswered && subsOk ? 1 : 0);
    }, 0);
  }, [groupedQuestions]);

  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  const missing = useMemo(() => {
    let miss = 0;
    for (const { parent, subQuestions } of groupedQuestions) {
      const answered = isAnswered(parent);
      const attachmentRequired = isAttachmentRequiredByBackend(parent);
      const attachOk = !attachmentRequired || hasBackAttachments(parent) || hasLocalFiles(parent);
      if (!answered || !attachOk) {
        miss += 1;
        continue;
      }
      const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));
      for (const sub of visibleSubs) {
        if (!sub.required) continue;
        const subAnswered = isAnswered(sub);
        const subAttachmentRequired = isAttachmentRequiredByBackend(sub);
        const subAttachOk = !subAttachmentRequired || hasBackAttachments(sub) || hasLocalFiles(sub);
        if (!subAnswered || !subAttachOk) {
          miss += 1;
          break;
        }
      }
    }
    return miss;
  }, [groupedQuestions]);

  const fetchChecklist = useCallback(async () => {
    if (!id || injectedQuestions) {
      setModel(injectedQuestions ? { id: id ?? null, name: checklistTitle || 'Responder Checklist' } : null);
      const normalizedInjected = (injectedQuestions || []).map((q) => normalizePlateAnswer(q));
      setQuestions(normalizedInjected);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { data } = await cooperplaceApi.get(`/single-checklist/${id}/response`);

      setModel(data);

      // Processa perguntas para adicionar questionTrigger
      const rawQuestions = data?.questionsResponses || [];
      const processedQuestions = rawQuestions.map(q =>
        isParentQuestion(q) ? processParentQuestion(q, rawQuestions) : q
      );

      const normalizedQuestions = processedQuestions.map((q) => normalizePlateAnswer(q));
      setQuestions(normalizedQuestions);
    } catch (e) {

      setError('Não foi possível carregar as perguntas para responder.');
      setModel(null);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [id, injectedQuestions, checklistTitle]);

  useEffect(() => {
    if (open) {
      fetchChecklist();
      return;
    }

    setError('');
    setModel(null);
    setQuestions([]);
    setResponseSaved(false);
    setSaving(false);
    setLoading(false);
  }, [open, fetchChecklist]);

  function updateQuestion(questionId, fn) {
    setQuestions((prev) => {
      const next = (prev || []).map((q) => {
        if (q.id !== questionId) return q;

        const cloned = {
          ...q,
          responses: Array.isArray(q.responses) ? q.responses.map((r) => ({ ...r })) : [],
          attachments: Array.isArray(q.attachments) ? [...q.attachments] : [],
          attachment: q.attachment ? { ...q.attachment } : {},
        };

        fn(cloned);
        return cloned;
      });

      return next;
    });
  }

  function removeSingleFile(questionId, fileIndex) {
    updateQuestion(questionId, (q) => {
      const filesArr = Array.from(q?.attachment?.files || []);
      filesArr.splice(fileIndex, 1);

      q.attachment = {
        ...q.attachment,
        files: filesArr.length ? filesArr : null,
      };
    });
  }

  function toggleUnique(questionId, optionId) {
    updateQuestion(questionId, (q) => {
      q.responses = (q.responses || []).map((r) => ({
        ...r,
        selected: r.id === optionId,
      }));
    });
  }

  function toggleMultiple(questionId, optionId) {
    updateQuestion(questionId, (q) => {
      q.responses = (q.responses || []).map((r) => {
        if (r.id !== optionId) return r;
        return { ...r, selected: !r.selected };
      });
    });
  }

  function setDescriptive(questionId, value) {
    updateQuestion(questionId, (q) => {
      q.questionDescriptiveResponse = value;
      const slug = getQuestionSlug(q);
      if (slug === 'code' || slug === 'code_reader') {
        q.code = value || null;
      }
    });
  }

  function setPlate(questionId, value) {
    updateQuestion(questionId, (q) => {
      const slug = getQuestionSlug(q);
      const plate = safeStr(value?.plate).toUpperCase();
      q.plateOption = value ? { ...value, plate } : null;
      q.questionDescriptiveResponse = plate;
      if (slug === 'vehicle_plate') {
        q.vehicleId = value?.id || null;
        q.wagonId = null;
      }
      if (slug === 'wagon_plate' || slug === 'implement_plate') {
        q.wagonId = value?.id || null;
        q.vehicleId = null;
      }
    });
  }

  function openRegisterVehicle(questionId) {
    setCurrentVehicleQuestionId(questionId);
    registerVehicle.open();
  }

  function openRegisterWagon(questionId) {
    setCurrentWagonQuestionId(questionId);
    registerWagon.open();
  }

  function handleRegisterVehicleResponse(response) {
    const vehicle = response?.data?.vehicle || response?.data;
    if (!vehicle || !currentVehicleQuestionId) return;
    setPlate(currentVehicleQuestionId, {
      id: vehicle?.id,
      plate: vehicle?.plate,
      vehicle,
    });
    setCurrentVehicleQuestionId(null);
  }

  function handleRegisterWagonResponse({ response }) {
    const wagon = response?.data;
    if (!wagon || !currentWagonQuestionId) return;
    setPlate(currentWagonQuestionId, wagon);
    setCurrentWagonQuestionId(null);
  }

  function handleCloseRegisterVehicle() {
    registerVehicle.close();
    setCurrentVehicleQuestionId(null);
  }

  function handleCloseRegisterWagon() {
    registerWagon.close();
    setCurrentWagonQuestionId(null);
  }

  function setUploadFiles(questionId, files) {
    updateQuestion(questionId, (q) => {
      q.attachment = { files, questionId: q.id };
      q.error = null;
      q.mediaError = null;
    });
  }

  function validateBeforeSend() {

    for (const { parent, subQuestions } of groupedQuestions) {

      if (parent.required && !isAnswered(parent)) {
        return `Responda as perguntas obrigatórias`;
      }
      const attachmentRequired = isAttachmentRequiredByBackend(parent);
      if (attachmentRequired && !hasBackAttachments(parent) && !hasLocalFiles(parent)) {
        return `Esta pergunta exige anexo: "${parent.question}"`;
      }
      const visibleSubs = subQuestions.filter((sq) => shouldShowSubQuestion(parent, sq));
      for (const sub of visibleSubs) {
        if (sub.required && !isAnswered(sub)) {
          return `Responda a subpergunta obrigatória: "${sub.question}"`;
        }
        const subAttachmentRequired = isAttachmentRequiredByBackend(sub);
        if (subAttachmentRequired && !hasBackAttachments(sub) && !hasLocalFiles(sub)) {
          return `Esta subpergunta exige anexo: "${sub.question}"`;
        }
      }
    }
    return '';
  }

  async function sendMedias(entries, singleChecklistId) {
    const formData = new FormData();
    if (singleChecklistId) formData.append('singleChecklistId', String(singleChecklistId));
    appendFilesToFormData(formData, entries);

    try {
      await cooperplaceApi.post('/single-checklist/response/media', formData);
    } catch (err) {
      const msg = String(err?.response?.data?.error || err?.response?.data?.message || err?.message || '');

      if (msg) {
        snackbar.show(<Text>{msg}</Text>, { type: 'error' });
      }

      throw err;
    }
  }

  async function answerChecklist(payloadQuestions, singleChecklistId) {
    await cooperplaceApi.post('/single-checklist/response', {
      singleChecklistId,
      questions: payloadQuestions,
    });
  }

  function clearUploadedFiles(entries) {
    const uploadedIds = new Set(entries.map((entry) => entry.questionId));
    setQuestions((prev) =>
      (prev || []).map((q) =>
        uploadedIds.has(q.id) ? { ...q, attachment: { ...q.attachment, files: null } } : q
      )
    );
  }

  async function handleSubmit() {
    if (!model) return;
    if (saving || submitInFlightRef.current) return;

    setError('');
    const msg = validateBeforeSend();
    if (msg) {
      setError(msg);
      return;
    }

    submitInFlightRef.current = true;
    setSaving(true);

    // Resposta primeiro, mídia depois: uma nova tentativa reenvia só o que ainda não foi gravado.
    let saved = responseSaved;

    try {
      const entries = collectFilesToUpload(groupedQuestions);
      const uploadIds = new Set(entries.map((entry) => entry.questionId));

      if (!saved) {
        const payloadQuestions = questions.map((q) => {
          const rawDesc =
            typeof q.questionDescriptiveResponse === 'string'
              ? q.questionDescriptiveResponse.trim()
              : q.questionDescriptiveResponse;
          const normalizedDesc = rawDesc === '' ? null : rawDesc;

          return {
            id: q.id,
            question: q.question,
            required: !!q.required,
            needAttachment: !!q.needAttachment,
            sentAttachment: uploadIds.has(q.id) || hasBackAttachments(q),
            questionDescriptiveResponse: normalizedDesc ?? null,
            vehicleId: q.vehicleId ?? null,
            wagonId: q.wagonId ?? null,
            code: q.code ?? null,
            type: q.type,
            responses: (q.responses || []).map((r) => ({
              id: r.id,
              order: r.order,
              selected: !!r.selected,
              description: r.description,
            })),
          };
        });

        if (typeof onSubmit === 'function') onSubmit({ id: model.id, questions: payloadQuestions });

        await answerChecklist(payloadQuestions, model.id);
        saved = true;
        setResponseSaved(true);
      }

      if (entries.length) {
        await sendMedias(entries, model.id);
        clearUploadedFiles(entries);
      }

      if (typeof onSuccess === 'function') onSuccess();
      if (typeof onClose === 'function') onClose();
    } catch (e) {
      if (saved) {
        setError('Respostas salvas. Não foi possível enviar os anexos, tente novamente.');
      } else {
        const backMsg = e?.response?.data?.error || e?.response?.data?.message || '';
        setError(backMsg ? safeStr(backMsg) : 'Não foi possível enviar as respostas do checklist.');
      }
    } finally {
      submitInFlightRef.current = false;
      setSaving(false);
    }
  }

  const headerTitle = checklistTitle || model?.name || 'Responder Checklist';
  const displayTitle = useMemo(() => {
    const currentId = model?.id || id;
    if (!currentId) return headerTitle;

    const normalizedTitle = String(headerTitle || '').trim();
    if (normalizedTitle.startsWith(`#${currentId}`)) return normalizedTitle;

    return `#${currentId} ${normalizedTitle}`;
  }, [model, id, headerTitle]);

  if (!open) return null;

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <RegisterVehicleModal
        isOpen={registerVehicle.isOpen}
        onClose={handleCloseRegisterVehicle}
        sendResponse={handleRegisterVehicleResponse}
      />
      <RegisterWagon
        isOpen={registerWagon.isOpen}
        onClose={handleCloseRegisterWagon}
        wagonKey="implement"
        sendResponse={handleRegisterWagonResponse}
      />
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={styles.headerLeft}>
              <div style={styles.title}>{displayTitle}</div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (typeof onClose === 'function') onClose();
              }}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 20,
                cursor: 'pointer',
                lineHeight: 1,
                marginLeft: 12,
                color: '#111827',
              }}
              disabled={saving}
            >
              ✕
            </button>
          </div>

          <div style={styles.headerBottom}>
            <div style={styles.counterPill}>
              {answeredCount} de {total} respondidas
            </div>
          </div>

          <div style={styles.progressTrack}>
            <div style={styles.progressFill(pct)} />
          </div>
        </div>

        <div style={styles.body}>
          {!!error && (
            <div
              style={{
                padding: 14,
                borderRadius: 12,
                background: '#FEF2F2',
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Text color="danger" weight={700}>
                {error}
              </Text>
            </div>
          )}

          {loading ? (
            <Text>Carregando perguntas...</Text>
          ) : total === 0 ? (
            <Text color="gray">Nenhuma pergunta encontrada.</Text>
          ) : (
            <div style={styles.grid}>
              {groupedQuestions.map(({ parent, subQuestions }, idx) => (
                <QuestionCard
                  key={parent.id}
                  q={parent}
                  idx={idx}
                  saving={saving}
                  hasError={!!error && parent.required && !isAnswered(parent)}
                  subQuestions={subQuestions}
                  shouldShowSubQuestion={shouldShowSubQuestion}
                  onToggleUnique={toggleUnique}
                  onToggleMultiple={toggleMultiple}
                  onSetDescriptive={setDescriptive}
                  onSetPlate={setPlate}
                  onSetUpload={setUploadFiles}
                  onRemoveFile={removeSingleFile}
                  onOpenRegisterVehicle={openRegisterVehicle}
                  onOpenRegisterWagon={openRegisterWagon}
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <div style={styles.leftFooter}>
            <div style={styles.missing}>Faltam {missing} pergunta(s)</div>
          </div>

          <div style={styles.rightFooter}>
            {onAnnul && !responseSaved && (
              <Button
                variant="error"
                onClick={() => {
                  if (typeof onAnnul === 'function') onAnnul();
                }}
                disabled={saving}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BlockOutlinedIcon style={{ fontSize: 18 }} />
                  <Text type="small" weight={700}>
                    Anular checklist
                  </Text>
                </div>
              </Button>
            )}

            <Button
              onClick={handleSubmit}
              disabled={loading || saving || total === 0}
              loading={saving}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SendOutlinedIcon style={{ fontSize: 18 }} />
                <Text type="small" weight={800}>
                  {responseSaved ? 'Reenviar anexos' : 'Enviar Respostas'}
                </Text>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
